import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '../utils/api';
import HeroSlider from '../components/HeroSlider';

function Home({ onAddToCart, searchTerm: navbarSearchTerm, onCategoryChange }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const observer = useRef();
  const searchTimeout = useRef(null);

  const lastProductRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [page]);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      if (navbarSearchTerm) {
        searchProducts(navbarSearchTerm);
      } else {
        filterProducts();
      }
    }, 300);
  }, [navbarSearchTerm, selectedCategory, products]);

  const searchProducts = async (term) => {
    setLoading(true);
    try {
      const response = await fetch(`${api.baseURL}/products.php?search=${encodeURIComponent(term)}`);
      const data = await response.json();
      if (data.products && Array.isArray(data.products)) {
        setFilteredProducts(data.products);
      } else {
        setFilteredProducts([]);
      }
    } catch (error) {
      console.error('Error searching products:', error);
      setFilteredProducts([]);
    }
    setLoading(false);
  };

  const loadProducts = async () => {
    if (loading) return;
    // Skip API calls during pre-rendering
    if (navigator.userAgent === 'ReactSnap') return;
    
    setLoading(true);
    try {
      const response = await fetch(`${api.baseURL}/products.php?page=${page}&limit=20`);
      const data = await response.json();
      
      if (data.products && Array.isArray(data.products)) {
        setProducts(prev => [...prev, ...data.products]);
        setHasMore(data.hasMore);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setHasMore(false);
    }
    setLoading(false);
  };

  const loadCategories = async () => {
    // Skip API calls during pre-rendering
    if (navigator.userAgent === 'ReactSnap') return;
    
    const data = await api.getCategories();
    setCategories(data);
  };

  const filterProducts = () => {
    let filtered = products;

    if (selectedCategory) {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    setFilteredProducts(filtered);
  };

  const getCategoryProducts = (categoryName) => {
    return products.filter(p => p.category === categoryName);
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategory(expandedCategory === categoryName ? null : categoryName);
  };

  return (
    <>
      <Helmet>
        <title>Loom Electronic Parts | Buy Loom Spare Parts Online Pakistan</title>
        <meta name="description" content="Buy genuine loom electronic parts, spare parts, and accessories online in Pakistan. Quality engine parts, electrical components, brake parts for textile looms with fast delivery across Lahore, Karachi, Islamabad." />
        <meta name="keywords" content="power loom parts, electronic parts, loom spare parts, textile machinery parts, engine parts, electrical parts, brake parts, Pakistan, online store, buy loom parts Lahore, Karachi, Islamabad" />
        <meta property="og:title" content="Loom Electronic Parts | Loom Spare Parts Store Pakistan" />
        <meta property="og:description" content="Quality loom spare parts with fast delivery. Engine parts, electrical parts, brake parts for all types of power looms." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://loomelectronicparts.com/" />
        <meta property="og:image" content="https://loomelectronicparts.com/logo.png" />
        <link rel="canonical" href="https://loomelectronicparts.com/" />
      </Helmet>
      <HeroSlider />
      <div className="home-layout">
      
      {/* Mobile Categories Button */}
      <button 
        className="mobile-categories-btn"
        onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
      >
        ☰ Categories
      </button>

      <aside className={`sidebar ${mobileCategoriesOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header-mobile">
          <h3 className="sidebar-title">Categories</h3>
          <button className="close-sidebar-btn" onClick={() => setMobileCategoriesOpen(false)}>×</button>
        </div>
        <div className="category-tree">
          {categories.map((cat) => {
            const categoryProducts = getCategoryProducts(cat.name);
            const isExpanded = expandedCategory === cat.name;
            
            return (
              <div key={cat.id} className="category-node">
                <div 
                  className={`category-header ${selectedCategory === cat.name ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedCategory(cat.name);
                    toggleCategory(cat.name);
                    if (onCategoryChange) onCategoryChange(cat.name);
                  }}
                >
                  <span className="category-name">{cat.name}</span>
                  <span className="category-count">({categoryProducts.length})</span>
                  <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
                </div>
                {isExpanded && (
                  <div className="product-dropdown">
                    {categoryProducts.map((prod) => (
                      <div 
                        key={prod.id} 
                        className="product-item-sidebar"
                        onClick={(e) => {
                          e.stopPropagation();
                          const slug = prod.slug || `product-${prod.id}`;
                          navigate(`/product/${slug}`);
                        }}
                        style={{cursor: 'pointer'}}
                      >
                        {prod.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button 
          className="clear-filter-btn"
          onClick={() => {
            setSelectedCategory('');
            setExpandedCategory(null);
            if (onCategoryChange) onCategoryChange('');
            setMobileCategoriesOpen(false);
          }}
        >
          Clear Filter
        </button>
      </aside>

      {mobileCategoriesOpen && (
        <div 
          className="categories-overlay"
          onClick={() => setMobileCategoriesOpen(false)}
        />
      )}

      <main className="main-content">
        <div className="container">
          <h1 className="page-title">Premium Spare Parts</h1>
          <p className="page-subtitle">Quality parts for your vehicle</p>

          <div className="products-grid">
            {filteredProducts.map((product, index) => (
              <div 
                key={product.id} 
                className="product-card" 
                onClick={() => {
                  const slug = product.slug || `product-${product.id}`;
                  navigate(`/product/${slug}`);
                }} 
                style={{cursor: 'pointer', position: 'relative'}}
                ref={filteredProducts.length === index + 1 ? lastProductRef : null}
              >
                {!product.in_stock && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: '#f44336',
                    color: '#fff',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '700',
                    zIndex: 1
                  }}>
                    OUT OF STOCK
                  </div>
                )}
                <img 
                  src={product.image || 'https://placehold.co/300x200/e0e0e0/666?text=No+Image'} 
                  alt={`Buy ${product.name} - ${product.category} Loom Spare Part Pakistan`} 
                  className="product-image"
                  loading="lazy"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/300x200/e0e0e0/666?text=No+Image';
                  }}
                  style={{opacity: product.in_stock ? 1 : 0.6}}
                />
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <div 
                    className="product-description" 
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                  <div className="product-price">
                    {product.previous_price && parseFloat(product.previous_price) > parseFloat(product.price) ? (
                      <>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                          <span style={{textDecoration: 'line-through', color: '#999', fontSize: '14px'}}>Rs. {product.previous_price}</span>
                          <span style={{background: '#4caf50', color: '#fff', padding: '2px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: '700'}}>
                            {Math.round(((parseFloat(product.previous_price) - parseFloat(product.price)) / parseFloat(product.previous_price)) * 100)}% OFF
                          </span>
                        </div>
                        <span style={{color: '#f44336', fontWeight: '700', fontSize: '20px'}}>Rs. {product.price}</span>
                      </>
                    ) : (
                      <span>Rs. {product.price}</span>
                    )}
                  </div>
                  
                  {/* Product Stats */}
                  {(product.cart_count > 0 || product.sold_count > 0) && (
                    <div style={{display: 'flex', gap: '8px', marginTop: '8px', fontSize: '11px'}}>
                      {product.cart_count > 0 && (
                        <span style={{color: '#666', display: 'flex', alignItems: 'center', gap: '3px'}}>
                          <span style={{color: '#1976d2'}}>●</span> {product.cart_count} in cart
                        </span>
                      )}
                      {product.sold_count > 0 && (
                        <span style={{color: '#666', display: 'flex', alignItems: 'center', gap: '3px'}}>
                          <span style={{color: '#2e7d32'}}>●</span> {product.sold_count} sold
                        </span>
                      )}
                    </div>
                  )}
                  <button 
                    className="add-to-cart" 
                    onClick={(e) => {
                      e.stopPropagation();
                      if (product.in_stock) {
                        onAddToCart(product);
                      } else {
                        alert('This product is out of stock');
                      }
                    }}
                    style={{
                      background: product.in_stock ? '#ff9f00' : '#999',
                      cursor: product.in_stock ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {loading && (
            <div style={{textAlign: 'center', padding: '40px', fontSize: '16px', color: '#666'}}>
              <div style={{display: 'inline-block', width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #ffc107', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></div>
              <p style={{marginTop: '16px'}}>Loading more products...</p>
            </div>
          )}
          
          {!loading && !hasMore && filteredProducts.length > 0 && (
            <div style={{textAlign: 'center', padding: '40px', fontSize: '16px', color: '#666', fontWeight: '600'}}>
              🎉 You've seen all products!
            </div>
          )}
        </div>
      </main>
    </div>
    </>
  );
}

export default Home;
